/**
 * Additive merge of widget payload data for pricing aggregates.
 *
 * `collectWidgetPayloads` used to merge every widget's `data` with a bare
 * `Object.assign`, which is last-writer-wins. That is correct for ordinary
 * settings keys (`aspect_ratio`, `guidance_scale`, …) — a workflow has one
 * widget owning each — but WRONG for the pricing aggregates below, which
 * describe *all* input media of the request and can be emitted by more than
 * one widget at a time:
 *
 *   - the media widgets (`Slots Input Media`, `Input Media`) emit them from
 *     their own slots;
 *   - `PromptEditorV2Widget` emits them for session references.
 *
 * With last-writer-wins, two widgets each holding a video meant only ONE
 * duration reached the pricing formula — a silent, ref-order-dependent
 * undercharge on a money path.
 *
 * Semantics mirror how a single media widget already aggregates ACROSS ITS OWN
 * SLOTS (see `BigSlotsInputMediaWidget.getPayloadData`): durations add up, and
 * the width/height/megapixel triplet comes from the largest-megapixel frame.
 *
 * Deliberately NOT included: `slots_image_count` / `slots_video_count` /
 * `slots_audio_count` / `slots_model_count`. Those describe Slots Input Media
 * specifically and are re-narrowed per request server-side by
 * `narrowPayloadForSlot`; summing them across widgets would change
 * `single_per_request` billing, which is outside this fix.
 */

const DURATION_PRICING_KEYS = ['video_duration', 'audio_duration'] as const;
export const EXACT_DURATION_PRICING_KEYS = ['video_duration_exact', 'audio_duration_exact'] as const;

/** Media aggregates that must be SUMMED across widgets. */
export const SUMMED_MEDIA_AGGREGATE_KEYS = [
  ...DURATION_PRICING_KEYS,
  ...EXACT_DURATION_PRICING_KEYS,
  'video_frame_count',
  'video_size_bytes',
] as const;

/** Media aggregates that use maximum semantics when emitted individually. */
export const MAX_MEDIA_AGGREGATE_KEYS = [
  'video_width',
  'video_height',
  'video_mpx',
  'video_fps',
] as const;

export type DurationPricingKey = typeof DURATION_PRICING_KEYS[number];

/**
 * Preserve the Input Media widget's historical "sum raw durations, round once"
 * contract while still billing every positive sub-second item for at least one
 * second. Rounding every item first changes ordinary multi-item prices (for
 * example, two 1.4-second clips become 2 instead of the historical 3).
 */
export function sumRoundedMediaDurations(durations: readonly number[]): number {
  const total = durations.reduce(
    (sum, duration) => sum + (duration > 0 && duration < 1 ? 1 : duration),
    0,
  );
  return Math.round(total);
}

/** Sum measured durations without a per-item minimum, rounding up to hundredths. */
export function sumExactMediaDurations(durations: readonly number[]): number {
  const total = durations.reduce(
    (sum, duration) => sum + (Number.isFinite(duration) && duration > 0 ? duration : 0),
    0,
  );
  // Absorb floating-point noise when the sum is already a whole hundredth.
  return total > 0 ? Math.ceil(total * 100 - 1e-6) / 100 : 0;
}

/**
 * Return duration fields whose selected media cannot be priced safely.
 *
 * Media widgets add gallery items to state before their asynchronous metadata
 * probe settles, and historically also kept an item when that probe failed.
 * In both states an absent duration must block generation: optional pricing
 * variables interpret it as "no media" and would otherwise undercharge.
 */
export function getUnmeasuredMediaPricingKeys(
  items: ReadonlyArray<{ mediaType: string; duration?: number }>,
): DurationPricingKey[] {
  const missing = new Set<DurationPricingKey>();

  for (const item of items) {
    if (item.mediaType !== 'video' && item.mediaType !== 'audio') continue;
    if (typeof item.duration === 'number' && Number.isFinite(item.duration) && item.duration > 0) {
      continue;
    }
    missing.add(item.mediaType === 'video' ? 'video_duration' : 'audio_duration');
  }

  return DURATION_PRICING_KEYS.filter(key => missing.has(key));
}

const VIDEO_DIMENSION_PRICING_KEYS = ['video_width', 'video_height', 'video_mpx'] as const;

/** Aggregates whose absence or invalidity can undercharge a request. */
export const BILLING_PRICING_KEYS: readonly string[] = [
  ...DURATION_PRICING_KEYS,
  ...EXACT_DURATION_PRICING_KEYS,
  ...VIDEO_DIMENSION_PRICING_KEYS,
];

function isValidPricingAggregate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Merge one widget's payload `data` into the combined payload.
 *
 * Contract:
 *  - Keys in `SUMMED_MEDIA_AGGREGATE_KEYS` accumulate: target = target + source.
 *  - Keys in `MAX_MEDIA_AGGREGATE_KEYS` take the larger of the two.
 *  - Every other key keeps the existing last-writer-wins behaviour.
 *  - Widgets only emit an aggregate when it is > 0, so an ABSENT key means
 *    "this widget contributed no such media" and must never be read as 0
 *    overwriting a real value.
 *
 * @param target combined payload being built (mutated in place)
 * @param source one widget's `getPayloadData().data`
 * @returns aggregate keys whose source values were invalid and were not assigned
 */
export function assignWidgetPayloadData(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): string[] {
  const rejectedKeys: string[] = [];
  const handledKeys = new Set<string>();

  // Real media widgets emit the three video dimension fields together. Treat
  // that complete group atomically so crossed aspect ratios cannot produce a
  // fictional frame (for example, width from one video and height from another).
  // The partial-key path below remains for backward compatibility with widgets
  // or tests that intentionally emit only one dimension scalar.
  const hasCompleteVideoDimensions = VIDEO_DIMENSION_PRICING_KEYS.every(key =>
    Object.prototype.hasOwnProperty.call(source, key)
  );
  const hasAnySourceVideoDimensions = VIDEO_DIMENSION_PRICING_KEYS.some(key =>
    Object.prototype.hasOwnProperty.call(source, key)
  );
  const targetHasAnyVideoDimensions = VIDEO_DIMENSION_PRICING_KEYS.some(key =>
    isValidPricingAggregate(target[key])
  );
  const targetHasCompleteVideoDimensions = VIDEO_DIMENSION_PRICING_KEYS.every(key =>
    isValidPricingAggregate(target[key])
  );
  const sourceVideoDimensionKeys = VIDEO_DIMENSION_PRICING_KEYS.filter(key =>
    Object.prototype.hasOwnProperty.call(source, key)
  );
  const targetVideoDimensionKeys = VIDEO_DIMENSION_PRICING_KEYS.filter(key =>
    isValidPricingAggregate(target[key])
  );
  const hasMatchingPartialVideoDimensionKeys =
    sourceVideoDimensionKeys.length === targetVideoDimensionKeys.length
    && sourceVideoDimensionKeys.every(key => targetVideoDimensionKeys.includes(key));

  // A complete + partial merge, or partial groups with different key sets, has
  // incompatible provenance and can create a full-looking triplet whose width,
  // height and megapixel count describe different frames. Matching legacy scalar
  // sets retain max semantics. Reject incompatible incoming dimensions instead;
  // the collector turns this into a fail-closed pricing error.
  const mixesIncompatibleVideoDimensions =
    hasAnySourceVideoDimensions
    && targetHasAnyVideoDimensions
    && (
      hasCompleteVideoDimensions !== targetHasCompleteVideoDimensions
      || (
        !hasCompleteVideoDimensions
        && !targetHasCompleteVideoDimensions
        && !hasMatchingPartialVideoDimensionKeys
      )
    );

  if (mixesIncompatibleVideoDimensions) {
    for (const key of VIDEO_DIMENSION_PRICING_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
      handledKeys.add(key);
      rejectedKeys.push(key);
    }
  }

  if (hasCompleteVideoDimensions && !mixesIncompatibleVideoDimensions) {
    const invalidDimensionKeys = VIDEO_DIMENSION_PRICING_KEYS.filter(key =>
      !isValidPricingAggregate(source[key])
    );

    handledKeys.add('video_width');
    handledKeys.add('video_height');
    handledKeys.add('video_mpx');

    if (invalidDimensionKeys.length > 0) {
      rejectedKeys.push(...invalidDimensionKeys);
    } else {
      const sourceMpx = source.video_mpx as number;
      if (!targetHasCompleteVideoDimensions) {
        target.video_width = source.video_width;
        target.video_height = source.video_height;
        target.video_mpx = sourceMpx;
      } else if (sourceMpx > (target.video_mpx as number)) {
        target.video_width = source.video_width;
        target.video_height = source.video_height;
        target.video_mpx = sourceMpx;
      }
    }
  }

  for (const [key, value] of Object.entries(source)) {
    if (handledKeys.has(key)) continue;

    const isSummedAggregate = SUMMED_MEDIA_AGGREGATE_KEYS.some(
      aggregateKey => aggregateKey === key,
    );
    const isMaxAggregate = MAX_MEDIA_AGGREGATE_KEYS.some(
      aggregateKey => aggregateKey === key,
    );

    if (!isSummedAggregate && !isMaxAggregate) {
      target[key] = value;
      continue;
    }

    if (!isValidPricingAggregate(value)) {
      rejectedKeys.push(key);
      continue;
    }

    const currentValue = target[key];
    if (typeof currentValue !== 'number' || !Number.isFinite(currentValue)) {
      target[key] = value;
    } else if (isSummedAggregate) {
      const rawSum = currentValue + value;
      const sum = EXACT_DURATION_PRICING_KEYS.some(exactKey => exactKey === key)
        ? Math.round(rawSum * 100) / 100
        : rawSum;
      if (!Number.isFinite(sum)) {
        rejectedKeys.push(key);
        continue;
      }
      target[key] = sum;
    } else {
      target[key] = Math.max(currentValue, value);
    }
  }

  return rejectedKeys;
}
